import { auth, firestore, storage } from '../firebase-config';
import React, { useState, useEffect, useCallback } from 'react';
import { DeleteConfirmation, MediaPreviewModal } from './widgets';
import { AppMode, dark_mode_primary, light_mode_primary } from './values';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy, where } from 'firebase/firestore';

export function Notes({ appMode }) {
    const userId = auth.currentUser.uid;
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');//eslint-disable-next-line
    const [mediaUrl, setMediaUrl] = useState('');
    const [showMenu, setShowMenu] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [mediaFile, setMediaFile] = useState(null);
    const [popupType, setPopupType] = useState('Note');
    const [editingNote, setEditingNote] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [noteToDelete, setNoteToDelete] = useState(null);
    const [previewMedia, setPreviewMedia] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [notificationTimers, setNotificationTimers] = useState({});
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [reminderDateTime, setReminderDateTime] = useState(new Date());
    const [notificationPermission, setNotificationPermission] = useState(Notification.permission);

    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/notifications-sw.js')
                .then(registration => {
                    Notification.requestPermission().then(permission => {
                        if (permission === 'granted') { }
                        else { }
                    });
                }).catch(error => { });
        }
    }, []);

    useEffect(() => {
        if (navigator.serviceWorker.controller && notes.length > 0) {
            navigator.serviceWorker.controller.postMessage({ type: 'CHECK_REMINDERS' });
        }
    }, [notes]);

    const fetchNotes = async () => {
        try {
            const notesQuery = query(
                collection(firestore, "notes"),
                where("userId", "==", userId),
                orderBy("timestamp", "desc")
            );
            const querySnapshot = await getDocs(notesQuery);
            const notesList = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setNotes(notesList);
            return notesList;
        } catch (error) {
            return [];
        }
    };

    const showNotification = (note) => {
        if (Notification.permission === "granted") {
            const title = note.content.split('\n')[0] || 'Reminder';
            const body = note.content.split('\n').slice(1).join('\n') || 'Your reminder is due!';

            if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'SHOW_NOTIFICATION',
                    title: title,
                    body: body,
                    noteId: note.id
                });
            } else {
                const notification = new Notification(title, {
                    body: body,
                    icon: '/favicon.ico',
                    tag: note.id,
                });

                notification.onclick = function () {
                    window.focus();
                    setEditingNote(note);
                    setNewNote(note.content);
                    setPopupType(note.type || 'Reminder');
                    this.close();
                };
            }
        }
        else { }
    };

    useEffect(() => {
        fetchNotes().then(notesList => {
            notesList.forEach(note => {
                if (note.type === 'Reminder' && note.reminderDateTime) {
                    scheduleNotification(note);
                }
            });
        });
        return () => {
            Object.values(notificationTimers).forEach(timer => clearTimeout(timer));
        };//eslint-disable-next-line
    }, [userId]);

    const requestNotificationPermission = async () => {
        if (Notification.permission !== "granted" && Notification.permission !== "denied") {
            const permission = await Notification.requestPermission();
            setNotificationPermission(permission);
            return permission;
        }
        return Notification.permission;
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setMediaFile(e.target.files[0]);
        }
    };

    const uploadMedia = async () => {
        if (!mediaFile) return null;
        setIsUploading(true);
        setUploadProgress(0);
        try {
            const filePath = `notes/${userId}/${Date.now()}-${mediaFile.name}`;
            const storageRef = ref(storage, filePath);
            const uploadTask = uploadBytesResumable(storageRef, mediaFile);
            return new Promise((resolve, reject) => {
                uploadTask.on(
                    'state_changed',
                    (snapshot) => {
                        const progress = Math.round(
                            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                        );
                        setUploadProgress(progress);
                    },
                    (error) => {
                        setIsUploading(false);
                        reject(error);
                    },
                    async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setMediaUrl(downloadURL);
                        setIsUploading(false);
                        setMediaFile(null);
                        resolve(downloadURL);
                    }
                );
            });
        } catch (error) {
            setIsUploading(false);
            return null;
        }
    };

    const handleAddNote = async () => {
        if (newNote.trim() !== '') {
            try {
                setIsSaving(true);
                setIsUploading(mediaFile ? true : false);
                const timestamp = new Date();
                const tempNote = {
                    id: 'temp-' + timestamp.getTime(),
                    userId: userId,
                    type: popupType,
                    content: newNote,
                    timestamp: timestamp,
                    date: timestamp.toLocaleDateString(),
                    isTemp: true,
                };

                if (popupType === 'Reminder') {
                    tempNote.reminderDateTime = reminderDateTime;
                }
                if (!editingNote || editingNote.id.startsWith('temp-')) {
                    setNotes(prevNotes => [tempNote, ...prevNotes]);
                }
                let mediaDownloadURL = null;
                if (mediaFile) {
                    mediaDownloadURL = await uploadMedia();
                }

                const noteData = {
                    userId: userId,
                    type: popupType,
                    content: newNote,
                    timestamp: new Date(),
                    date: new Date().toLocaleDateString(),
                };

                if (mediaDownloadURL) {
                    noteData.mediaUrl = mediaDownloadURL;
                    noteData.mediaType = mediaFile.type.split('/')[0];
                    noteData.mediaName = mediaFile.name;
                } else if (editingNote && editingNote.mediaUrl) {
                    noteData.mediaUrl = editingNote.mediaUrl;
                    noteData.mediaType = editingNote.mediaType;
                    noteData.mediaName = editingNote.mediaName;
                }

                if (popupType === 'Reminder') {
                    noteData.reminderDateTime = reminderDateTime;
                }
                let noteRef;
                let savedNoteId;

                if (editingNote && !editingNote.id.startsWith('temp-')) {
                    noteRef = doc(firestore, "notes", editingNote.id);
                    await updateDoc(noteRef, noteData);
                    savedNoteId = editingNote.id;
                    noteData.id = editingNote.id;
                    setNotes(prevNotes => prevNotes.map(note =>
                        note.id === editingNote.id ? { ...noteData, id: editingNote.id } : note
                    ));
                } else {
                    const docRef = await addDoc(collection(firestore, "notes"), noteData);
                    noteRef = docRef;
                    savedNoteId = docRef.id;
                    noteData.id = docRef.id;
                    setNotes(prevNotes => prevNotes.map(note =>
                        note.id === tempNote.id ? { ...noteData, id: savedNoteId } : note
                    ));
                }

                if (popupType === 'Reminder') {
                    const permission = await requestNotificationPermission();
                    if (permission === "granted") {
                        await scheduleNotification({
                            ...noteData,
                            id: noteData.id
                        });
                    }
                    if (noteData.id) {
                        addReminder(noteData, reminderDateTime);
                    }
                }
                setNewNote('');
                setMediaFile(null);
                setMediaUrl('');
                setEditingNote(null);
                setIsUploading(false);
                setIsSaving(false);
            }
            catch (error) {
                setIsUploading(false);
                setIsSaving(false);
                if (!editingNote) {
                    setNotes(prevNotes => prevNotes.filter(note => !note.isTemp));
                }
            }
        } else {
            setEditingNote(null);
        }
    };

    function addReminder(note, reminderTime) {
        if (!note || typeof note.id !== 'string' || !note.id) {
            return;
        }

        const reminder = {
            id: note.id,
            title: note.content ? note.content.split('\n')[0].trim() || 'Reminder' : 'Reminder',
            body: note.content ? note.content.split('\n').slice(1).join('\n').trim() || 'Your reminder is due' : 'Your reminder is due',
            timestamp: new Date(reminderTime).getTime(),
            data: { noteId: note.id },
        };

        const request = indexedDB.open('reminders-db', 1);
        request.onerror = event => { };
        request.onupgradeneeded = event => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('reminders')) {
                const store = db.createObjectStore('reminders', { keyPath: 'id' });
                store.createIndex('by-timestamp', 'timestamp', { unique: false });
            }
        };

        request.onsuccess = event => {
            const db = event.target.result;
            try {
                const transaction = db.transaction(['reminders'], 'readwrite');
                const store = transaction.objectStore('reminders');
                const addRequest = store.add(reminder);
                addRequest.onerror = event => { };
                transaction.oncomplete = () => {
                    scheduleReminderCheck(reminderTime);
                };
                transaction.onerror = event => { };
            } catch (e) { }
        };
    }

    function scheduleReminderCheck(specificTime = null) {
        let delay = 60000;
        if (specificTime) {
            const timeUntilReminder = new Date(specificTime).getTime() - Date.now();
            if (timeUntilReminder > 0) {
                delay = Math.min(timeUntilReminder, 60000);
            }
        }
        setTimeout(() => {
            if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: 'CHECK_REMINDERS' });
            }
            scheduleReminderCheck();
        }, delay);
    }
    useEffect(() => {
        scheduleReminderCheck();
        return () => { };//eslint-disable-next-line
    }, []);

    const confirmDelete = (note) => {
        setNoteToDelete(note);
        setShowDeleteConfirm(true);
    };

    const handleDeleteNote = async () => {
        if (!noteToDelete) return;
        try {
            await deleteDoc(doc(firestore, "notes", noteToDelete.id));
            if (noteToDelete.mediaUrl) {
                try {
                    const mediaPath = noteToDelete.mediaUrl.split('?')[0].split('/o/')[1];
                    if (mediaPath) {
                        const decodedPath = decodeURIComponent(mediaPath);
                        const mediaRef = ref(storage, decodedPath);
                        await deleteObject(mediaRef);
                    }
                }
                catch (mediaError) { }
            }

            if (notificationTimers[noteToDelete.id]) {
                clearTimeout(notificationTimers[noteToDelete.id]);
                setNotificationTimers(prev => {
                    const updated = { ...prev };
                    delete updated[noteToDelete.id];
                    return updated;
                });
            }

            try {
                await deleteReminderFromDB(noteToDelete.id);
            }
            catch (err) { }
            setNotes(notes.filter(note => note.id !== noteToDelete.id));
            setShowDeleteConfirm(false);
            setNoteToDelete(null);
            if (editingNote && editingNote.id === noteToDelete.id) {
                setEditingNote(null);
            }
        }
        catch (error) { }
    };

    const handleNewItem = (type) => {
        const timestamp = new Date();
        const emptyNote = {
            id: 'temp-' + timestamp.getTime(),
            userId: userId,
            type: type,
            content: '',
            timestamp: timestamp,
            date: timestamp.toLocaleDateString(),
        };
        if (type === 'Reminder') {
            const defaultTime = new Date();
            defaultTime.setHours(defaultTime.getHours() + 1);
            emptyNote.reminderDateTime = defaultTime;
            setReminderDateTime(defaultTime);
        }
        setNewNote('');
        setPopupType(type);
        setTimeout(() => {
            setEditingNote({ ...emptyNote });
        }, 0);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleAddNote();
            setEditingNote(null);
        }
    };

    const filteredNotes = notes.filter(note =>
        note.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatReminderTime = (dateTime) => {
        if (!dateTime) return '';
        const date = dateTime.toDate ? dateTime.toDate() : new Date(dateTime);
        return date.toLocaleString();
    };

    const getReminderStatus = (reminderDateTime) => {
        if (!reminderDateTime) return 'none';
        const date = reminderDateTime.toDate ? reminderDateTime.toDate() : new Date(reminderDateTime);
        const now = new Date();

        if (date < now) {
            return 'past';
        } else {
            return 'upcoming';
        }
    };

    const formatInputDateTime = (date) => {
        const d = date instanceof Date ? date : new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const openRemindersDB = useCallback(() => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open('reminders-db', 1);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
            request.onupgradeneeded = event => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('reminders')) {
                    const store = db.createObjectStore('reminders', { keyPath: 'id' });
                    store.createIndex('by-timestamp', 'timestamp', { unique: false });
                }
            };
        });
    }, []);

    const handleRightClick = (e) => {
        e.preventDefault();
        setMenuPosition({ x: e.clientX, y: e.clientY });
        setShowMenu(true);
    };

    const storeReminder = useCallback(async (reminder) => {
        try {
            const db = await openRemindersDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(['reminders'], 'readwrite');
                const store = transaction.objectStore('reminders');
                const request = store.put(reminder);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve();
            });
        }
        catch (error) { }
    }, [openRemindersDB]);

    const deleteReminderFromDB = useCallback(async (id) => {
        try {
            const db = await openRemindersDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction(['reminders'], 'readwrite');
                const store = transaction.objectStore('reminders');
                const request = store.delete(id);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve();
            });
        }
        catch (error) { }
    }, [openRemindersDB]);

    const scheduleNotification = useCallback(async (note) => {
        if (notificationTimers[note.id]) {
            clearTimeout(notificationTimers[note.id]);
        }
        if (!note.reminderDateTime) return;
        const reminderTime = note.reminderDateTime.toDate ?
            note.reminderDateTime.toDate() :
            new Date(note.reminderDateTime);

        const currentTime = new Date();
        const timeUntilReminder = reminderTime - currentTime;
        try {
            await storeReminder({
                id: note.id,
                title: note.content.split('\n')[0] || 'Reminder',
                body: note.content.split('\n').slice(1).join('\n') || 'Your reminder is due!',
                timestamp: reminderTime.getTime(),
                data: { noteId: note.id }
            });

            if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({ type: 'CHECK_REMINDERS' });
            }
        }
        catch (error) { }
        if (timeUntilReminder > 0) {
            const timer = setTimeout(() => {
                showNotification(note);
            }, timeUntilReminder);
            setNotificationTimers(prev => ({
                ...prev,
                [note.id]: timer
            }));
        } else if (timeUntilReminder <= 0 && timeUntilReminder > -3600000) {
            showNotification(note);
        }
    }, [notificationTimers, storeReminder]);

    const NotificationPermissionBanner = () => {
        if (notificationPermission === "granted" || notificationPermission === "denied") {
            return null;
        }
        return (
            <div className={`px-4 py-3 mb-2 flex items-center justify-between rounded-lg shadow-sm ${appMode === AppMode.LIGHT ? 'bg-blue-50 text-blue-800 border border-blue-100' : 'bg-blue-900 bg-opacity-20 text-blue-200 border border-blue-800'} transition-all duration-200`}>
                <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span className="text-sm font-medium">Enable notifications for reminders</span>
                </div>
                <button onClick={requestNotificationPermission} className={`px-3 py-1.5 text-xs font-medium rounded-md shadow-sm hover:shadow transition-all duration-200 ${appMode === AppMode.LIGHT ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' : 'bg-blue-800 text-blue-100 hover:bg-blue-700'}`}>Enable</button>
            </div>
        );
    };
    return (
        <div className={`min-h-screen ${appMode === AppMode.LIGHT ? 'bg-gray-50' : dark_mode_primary}`}>
            <div className="flex h-screen">
                <div className={`w-72 border-r ${appMode === AppMode.LIGHT ? 'bg-white border-gray-200' : dark_mode_primary + ' border-gray-800'} flex flex-col shadow-lg transition-all duration-300`}>
                    <div className={`p-5 border-b ${appMode === AppMode.LIGHT ? 'border-gray-200' : 'border-gray-700'}`}>
                        <h1 className={`text-2xl font-bold mb-4 ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>My Notes</h1>
                        <div className="relative">
                            <input type="text" placeholder="Search notes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm bg-transparent ${appMode === AppMode.LIGHT ? 'border border-gray-200 text-gray-800' : 'border border-gray-700 text-white'} focus:outline-none focus:ring-2 ${appMode === AppMode.LIGHT ? 'focus:ring-blue-300 focus:border-blue-400' : 'focus:ring-blue-700 focus:border-blue-600'} shadow-sm transition-all duration-200`} />
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 absolute left-3 top-3 ${appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                        <NotificationPermissionBanner />
                        <div className={`sticky top-0 z-10 flex justify-between items-center px-4 py-3 ${appMode === AppMode.LIGHT ? 'bg-white border-b border-gray-100' : dark_mode_primary + ' border-b border-gray-800'}`}>
                            <span className={`text-xs font-semibold uppercase tracking-wider ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Notes</span>
                            <button onClick={() => handleNewItem('Note')} className={`p-1.5 rounded-full ${appMode === AppMode.LIGHT ? 'hover:bg-gray-100 text-blue-600 hover:text-blue-700' : 'hover:bg-gray-800 text-blue-400 hover:text-blue-300'} transition-colors duration-200`} title="Add new note">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-2">
                            {filteredNotes.filter(note => note.type === 'Note').map((note) => (
                                <div key={note.id} onClick={() => { setEditingNote(note); setNewNote(note.content); setPopupType(note.type || 'Note'); setMediaUrl(note.mediaUrl || ''); }} className={`mx-2 my-2 p-3.5 rounded-lg cursor-pointer ${editingNote && editingNote.id === note.id ? (appMode === AppMode.LIGHT ? 'bg-blue-50 border-blue-200 shadow-md' : 'bg-blue-900 bg-opacity-20 border-blue-800 shadow-md') : ''} ${appMode === AppMode.LIGHT ? 'hover:bg-gray-50 border border-gray-200 hover:shadow-md' : 'hover:bg-gray-800 border border-gray-700 hover:shadow-md'} transition-all duration-200`}>
                                    <div className={`font-medium text-sm mb-1.5 truncate ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{note.content.split('\n')[0] || 'New Note'}</div>
                                    {note.mediaUrl && (
                                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${appMode === AppMode.LIGHT ? 'text-blue-500' : 'text-blue-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                    )}
                                    <div className="flex items-center text-xs">
                                        <span className={`mr-2 ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>{note.date}</span>
                                        <span className={`truncate ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {note.content.split('\n').slice(1).join(' ').substring(0, 30)}
                                            {note.content.split('\n').slice(1).join(' ').length > 30 ? '...' : ''}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={`sticky top-0 z-10 flex justify-between items-center px-4 py-3 mt-4 ${appMode === AppMode.LIGHT ? 'bg-white border-b border-gray-100' : dark_mode_primary + ' border-b border-gray-800'}`}>
                            <span className={`text-xs font-semibold uppercase tracking-wider ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Reminders</span>
                            <button onClick={() => handleNewItem('Reminder')} className={`p-1.5 rounded-full ${appMode === AppMode.LIGHT ? 'hover:bg-amber-50 text-amber-600 hover:text-amber-700' : 'hover:bg-amber-900 hover:bg-opacity-20 text-amber-400 hover:text-amber-300'} transition-colors duration-200`} title="Add new reminder">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                        </div>
                        <div className="px-2">
                            {filteredNotes.filter(note => note.type === 'Reminder').map((note) => {
                                const reminderStatus = getReminderStatus(note.reminderDateTime);
                                return (
                                    <div key={note.id} onContextMenu={handleRightClick} onClick={() => { setEditingNote(note); setNewNote(note.content); setPopupType(note.type || 'Reminder'); if (note.reminderDateTime) { const date = note.reminderDateTime.toDate ? note.reminderDateTime.toDate() : new Date(note.reminderDateTime); setReminderDateTime(date); } }} className={`mx-2 my-2 p-3.5 rounded-lg cursor-pointer 
                                        ${editingNote && editingNote.id === note.id
                                            ? appMode === AppMode.LIGHT
                                                ? 'bg-amber-50 border-amber-200 shadow-md'
                                                : 'bg-amber-900 bg-opacity-20 border-amber-800 shadow-md'
                                            : ''
                                        }
                                        ${reminderStatus === 'past'
                                            ? appMode === AppMode.LIGHT
                                                ? 'opacity-70 bg-gray-50 border border-gray-200'
                                                : 'opacity-60 bg-gray-800 border border-gray-700'
                                            : appMode === AppMode.LIGHT
                                                ? 'hover:bg-amber-50 border border-amber-200 bg-amber-50 hover:shadow-md'
                                                : 'hover:bg-amber-900 hover:bg-opacity-20 border border-amber-800 bg-amber-900 bg-opacity-10 hover:shadow-md'
                                        } transition-all duration-200`}>
                                        <div className="flex items-center">
                                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 mr-2 ${reminderStatus === 'past' ? (appMode === AppMode.LIGHT ? 'text-gray-400' : 'text-gray-500') : (appMode === AppMode.LIGHT ? 'text-amber-500' : 'text-amber-400')}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                            </svg>
                                            <div className={`font-medium text-sm mb-0.5 truncate ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-white'}`}>{note.content.split('\n')[0] || 'New Reminder'}</div>
                                        </div>
                                        <div className="flex items-center text-xs ml-6">
                                            <span className={`mr-2 ${reminderStatus === 'past' ? (appMode === AppMode.LIGHT ? 'text-red-500 font-medium' : 'text-red-400 font-medium') : (appMode === AppMode.LIGHT ? 'text-green-600 font-medium' : 'text-green-400 font-medium')}`}>{note.reminderDateTime ? formatReminderTime(note.reminderDateTime) : note.date}</span>
                                            <span className={`truncate ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {note.content.split('\n').slice(1).join(' ').substring(0, 30)}
                                                {note.content.split('\n').slice(1).join(' ').length > 30 ? '...' : ''}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {showMenu && (
                            <div className={`absolute z-50 ${appMode === AppMode.LIGHT ? 'bg-white border border-gray-100 shadow-lg' : dark_mode_primary + ' border border-gray-700 shadow-lg shadow-gray-900'} rounded-lg p-2`} style={{ top: menuPosition.y, left: menuPosition.x }}>
                                <ul>
                                    <li className="p-2 hover:bg-red-50 rounded-md hover:text-red-700 cursor-pointer text-red-500 font-medium transition-colors duration-200 flex items-center" onClick={() => { }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg> Delete
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`flex-1 flex flex-col ${appMode === AppMode.LIGHT ? light_mode_primary : dark_mode_primary}`}>
                    {editingNote
                        ? <div className="flex-1 flex flex-col h-full">
                            <div className={`px-8 py-4 border-b ${appMode === AppMode.LIGHT ? 'border-gray-100 bg-white shadow-sm' : 'border-gray-800 bg-gray-900 bg-opacity-20 shadow-sm'} flex items-center justify-between`}>
                                <div className="flex items-center">
                                    <span className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {editingNote.id.startsWith('temp-') ? `New ${editingNote.type}` : `${editingNote.date} • ${editingNote.type}`}
                                        {editingNote.type === 'Reminder' && editingNote.reminderDateTime && <span className="ml-2 font-medium">• Due: {formatReminderTime(editingNote.reminderDateTime)}</span>}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <label className={`p-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${appMode === AppMode.LIGHT
                                        ? 'text-blue-600 hover:bg-blue-50 hover:text-blue-700'
                                        : 'text-blue-400 hover:bg-blue-900 hover:bg-opacity-20 hover:text-blue-300'
                                        }`}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                        </svg>
                                        <input type="file" className="hidden" onChange={handleFileChange} accept="image/*,audio/*,application/pdf" />
                                    </label>
                                    <button onClick={() => setEditingNote(null)} className={`p-2 rounded-md text-sm font-medium transition-colors ${appMode === AppMode.LIGHT ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-800' : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'}`}><i className="bi bi-x-lg"></i></button>
                                    {!editingNote.id.startsWith('temp-') && (
                                        <button onClick={() => confirmDelete(editingNote)} className={`p-2 rounded-md text-sm font-medium transition-colors ${appMode === AppMode.LIGHT ? 'text-red-600 hover:bg-red-50 hover:text-red-700' : 'text-red-400 hover:bg-red-900 hover:bg-opacity-20 hover:text-red-300'}`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                            <textarea value={newNote} onKeyDown={handleKeyDown} onChange={(e) => setNewNote(e.target.value)} className={`flex-1 w-full px-8 py-6 resize-none bg-transparent ${appMode === AppMode.LIGHT ? 'text-gray-800' : 'text-gray-100'} focus:outline-none leading-relaxed text-base`} placeholder="Start writing..."></textarea>
                            {(editingNote.mediaUrl || mediaFile) && (
                                <div className={`px-8 py-4 border-t ${appMode === AppMode.LIGHT ? 'border-gray-100 bg-white' : 'border-gray-800 bg-gray-900 bg-opacity-20'}`}>
                                    {isUploading
                                        ? <div className="w-full">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Uploading...</span>
                                                <span className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-600' : 'text-gray-400'}`}>{uploadProgress}%</span>
                                            </div>
                                            <div className="w-full bg-gray-200 rounded-full h-2">
                                                <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                                            </div>
                                        </div>
                                        : <div className="flex items-start">
                                            <div className="flex-1">
                                                {mediaFile
                                                    ? <div className="flex items-center">
                                                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 mr-2 ${appMode === AppMode.LIGHT ? 'text-blue-500' : 'text-blue-400'
                                                            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                        </svg>
                                                        <span className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{mediaFile.name}</span>
                                                    </div>
                                                    : editingNote.mediaUrl && (
                                                        <div className="relative cursor-pointer group" onClick={() => {
                                                            setPreviewMedia({
                                                                url: editingNote.mediaUrl,
                                                                type: editingNote.mediaType || 'file',
                                                                name: editingNote.mediaName,
                                                            });
                                                            setShowPreviewModal(true);
                                                        }}>
                                                            {editingNote.mediaType === 'image'
                                                                ? <img src={editingNote.mediaUrl} className="max-h-48 rounded-lg shadow-sm object-contain bg-gray-100" alt='' />
                                                                : editingNote.mediaType === 'video'
                                                                    ? <div className="relative">
                                                                        <video src={editingNote.mediaUrl} className="max-h-48 rounded-lg shadow-sm" />
                                                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all rounded-lg">
                                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                            </svg>
                                                                        </div>
                                                                    </div>
                                                                    : <div className={`p-3 rounded-lg flex items-center ${appMode === AppMode.LIGHT ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-800 hover:bg-gray-700'} transition-colors`}>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                        </svg>
                                                                        <span className={`text-sm ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>{editingNote.mediaName || "Attached file"}</span>
                                                                    </div>
                                                            }
                                                        </div>
                                                    )}
                                            </div>
                                            <button onClick={(e) => {
                                                e.stopPropagation();
                                                setMediaFile(null);
                                                if (editingNote.mediaUrl) {
                                                    const updatedNote = { ...editingNote };
                                                    delete updatedNote.mediaUrl;
                                                    delete updatedNote.mediaType;
                                                    delete updatedNote.mediaName;
                                                    setEditingNote(updatedNote);
                                                }
                                            }} className={`ml-2 p-1.5 rounded-md text-sm font-medium transition-colors ${appMode === AppMode.LIGHT
                                                ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                                                : 'text-red-400 hover:bg-red-900 hover:bg-opacity-20 hover:text-red-300'
                                                }`}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    }
                                </div>
                            )}
                            {editingNote.type === 'Reminder' && (
                                <div className={`px-8 py-4 border-t ${appMode === AppMode.LIGHT ? 'border-gray-100 bg-white shadow-sm' : 'border-gray-800 bg-gray-900 bg-opacity-20 shadow-sm'}`}>
                                    <div className="flex items-center flex-wrap gap-3">
                                        <label className={`text-sm font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>Reminder Time:</label>
                                        <input type="datetime-local" value={formatInputDateTime(reminderDateTime)} onChange={(e) => {
                                            if (e.target.value) {
                                                setReminderDateTime(new Date(e.target.value));
                                            }
                                        }} className={`p-2 rounded-md border ${appMode === AppMode.LIGHT ? 'border-gray-300 bg-white text-gray-800 focus:ring-2 focus:ring-blue-200 focus:border-blue-400' : 'border-gray-600 bg-gray-700 text-white focus:ring-2 focus:ring-blue-800 focus:border-blue-600'} focus:outline-none transition-all duration-200`} />
                                        <button onClick={handleAddNote} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-sm hover:shadow ${appMode === AppMode.LIGHT ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-700 text-white hover:bg-blue-800'}`}>{editingNote.id.startsWith('temp-') ? 'Save Reminder' : 'Update Reminder'}</button>
                                    </div>
                                </div>
                            )}
                        </div>
                        : filteredNotes.length === 0
                            ? <div className="flex-1 flex flex-col items-center justify-center">
                                <div className={`w-20 h-20 mb-6 rounded-full flex items-center justify-center ${appMode === AppMode.LIGHT ? 'bg-gray-50 shadow-inner' : 'bg-gray-800 shadow-inner'}`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-10 w-10 ${appMode === AppMode.LIGHT ? 'text-gray-300' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <p className={`text-lg font-medium ${appMode === AppMode.LIGHT ? 'text-gray-700' : 'text-gray-300'}`}>No notes yet</p>
                                <p className={`text-sm mt-1 ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>Create a new note to get started</p>
                            </div>
                            : <div className={`flex flex-col items-center justify-center h-full ${appMode === AppMode.LIGHT ? 'text-gray-500' : 'text-gray-400'}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-lg font-medium mb-1">Select a note</p>
                                <p className="text-sm">Choose an item from the sidebar to view or edit</p>
                            </div>
                    }
                </div>
            </div>
            {isSaving && (
                <div className={`fixed bottom-4 right-4 z-50 px-4 py-2 rounded-md shadow-lg ${appMode === AppMode.LIGHT
                    ? 'bg-white border border-gray-200 text-gray-800'
                    : 'bg-gray-800 border border-gray-700 text-white'
                    }`}>
                    <div className="flex items-center space-x-2">
                        <div className={`animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 ${appMode === AppMode.LIGHT ? 'border-blue-500' : 'border-blue-400'}`}></div>
                        <span className="text-sm">Saving note...</span>
                    </div>
                </div>
            )}
            {showDeleteConfirm && noteToDelete && <DeleteConfirmation appMode={appMode} setShowDeleteConfirm={setShowDeleteConfirm} noteToDelete={noteToDelete} handleDeleteNote={handleDeleteNote} />}
            {showPreviewModal && previewMedia && <MediaPreviewModal media={previewMedia} onClose={() => setShowPreviewModal(false)} appMode={appMode} />}
        </div>
    );
}